
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Input, Select, Space, Switch, Table, Form, Popconfirm, InputNumber, Tag } from 'antd';
import NewStyleInput from './NewStyleInput';
import Tooltip from '../../common/Tooltip';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '../../../store/store';
import { ColorPaletteButton } from '../../common/ColorPaletteButton';
import DuotoneLightIcon from '../../common/DuotoneLightIcon';
import { iconButtonStyle } from '../../../utils/styles';
import './tableConfig.css';
import { BORDER_GRAY, defaultTableConfiguration, PRIMARY_DARK } from '../../../constants/constants';
import { useTranslation } from 'react-i18next';
import { maxFontSize, minFontSize } from '../../../constants/gridConfig';
import FontPicker from '../../common/FontPicker';
import { mergeConfigurationTableStyles } from '../../../utils/styles';
import { convertPadding, DEFAULT_TABLE_BORDER_WIDTH } from '../../../utils/table';
import { convertPaddingUnit, resolveCustomPadding, TABLE_PADDING_UNITS } from '../../../utils/tableStyles';
import { useSectionStylesFilter } from './utils';
import { cellsContrast, headerContrast, MIN_CONTRAST } from './tableContrast';
import { useShareWithConfigPreview } from '../../../contexts/ConfigPreviewContext';
import {
  addStyle, copyStyle, removeStyle, renameStyle, styleLocks, updateStyle,
} from '../../../shared/theme/styleList';

const selector = ({
  templates,
  configuration,
  setSelectedStyle,
  currentTemplate,
  createTemplateInStore,
  setConfiguration,
  postTemplate,
  tmp
}) => ({
  tableStyles: templates.filter((t) => t.pattern === 'style'),
  templates,
  configuration,
  setSelectedStyle,
  templatable: currentTemplate.templatable,
  createTemplateInStore,
  setConfiguration,
  postTemplate,
  tmp
});

// Intitulé à gauche du champ, chaque réglage tient sur une ligne et prend la
// largeur de son contenu : les réglages se rangent côte à côte, à la suite.
const cardFieldLayout = {
  labelCol: { flex: 'none' },
  wrapperCol: { flex: 'none' },
  labelAlign: 'left',
  colon: false,
};

// Largeurs des champs, selon ce qu'ils contiennent.
const CARD_NUMBER_WIDTH = 60;
// Taille personnalisée : sélecteur, valeur et unité tiennent sur la rangée des
// couleurs.
const CARD_CUSTOM_SELECT_WIDTH = 100;
const CARD_CUSTOM_VALUE_WIDTH = 64;
const CARD_UNIT_WIDTH = 88;
const CARD_UNIT_SELECT_WIDTH = 56;
const CARD_SELECT_WIDTH = 110;

// Carte d'une zone du tableau. Les badges de l'en-tête résument ses réglages :
// on lit l'état d'un style sans parcourir ses champs.
// `toggle` ({ checked, onChange, disabled }) met l'interrupteur de la zone dans
// l'en-tête : éteinte, la carte se réduit à cet en-tête, ses champs n'ayant
// plus d'effet.
const SettingsCard = ({ icon: iconName, title, badges, toggle, children }) => {
  const open = !toggle || toggle.checked;

  return (
    <section style={{ border: `1px solid ${BORDER_GRAY}`, borderRadius: 10 }}>
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 16px',
          borderBottom: open ? `1px solid ${BORDER_GRAY}` : undefined,
        }}
      >
        <DuotoneLightIcon name={iconName} style={{ fontSize: 16 }} />
        <span style={{ fontWeight: 'bold' }}>{title}</span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {badges}
          {toggle && (
            <Switch
              size='small'
              aria-label={title}
              checked={toggle.checked}
              disabled={toggle.disabled}
              onChange={toggle.onChange}
            />
          )}
        </span>
      </header>
      {/* Les réglages se suivent sur la rangée tant qu'ils y tiennent, puis
          passent à la suivante (une par rangée en Drawer compact). */}
      {open && (
        <div
          className='table-settings-card-body'
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 20,
            padding: '10px 16px 0',
          }}
        >
          {children}
        </div>
      )}
    </section>
  );
};

// Contraste le plus faible entre le texte d'une zone et ses fonds, au seuil
// AA du WCAG pour un texte courant.
const ContrastBadge = ({ ratio }) => {
  if (ratio === null) return null;
  const compliant = ratio >= MIN_CONTRAST;

  // Fond plein et texte blanc : les teintes pâles des Tag antd se perdaient
  // dans l'en-tête de carte.
  return (
    <Tag
      bordered={false}
      title={`Rapport de contraste entre le texte et le fond. ${MIN_CONTRAST}:1 minimum pour une lecture confortable.`}
      style={{
        marginInlineEnd: 0,
        backgroundColor: compliant ? '#389e0d' : '#d4380d',
        color: 'white',
        fontWeight: 600,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
      }}
    >
      <DuotoneLightIcon
        name={compliant ? 'check' : 'triangle-exclamation'}
        weight='solid'
        secondaryOpacity={0.6}
      />
      {compliant ? 'Contraste conforme' : 'Contraste faible'} · {ratio.toFixed(1)}:1
    </Tag>
  );
};

// En-tête de la liste des styles : même habillage que la liste des masques.
const styleListHeaderCell = () => ({
  style: { backgroundColor: PRIMARY_DARK, color: 'white', textAlign: 'center' },
});

// Environ cinq styles visibles, les suivants au défilement.
const STYLE_LIST_MAX_HEIGHT = 260;

export const TabsTables = () => {
  const { configuration, setConfiguration, tmp } = useStore(selector);
  const { t } = useTranslation();
  const tablesStyles = mergeConfigurationTableStyles(configuration, tmp)

  const [selectedStyle, setSelectedStyle] = useState(tablesStyles[0]);
  // Style choisi dans la liste, montré par la page d'aperçu du Drawer.
  useShareWithConfigPreview('tableStyleId', selectedStyle?.id);
  // Style choisi hors de la zone visible de la liste (création, duplication) :
  // la liste défile jusqu'à lui.
  const styleListRef = useRef(null);
  useEffect(() => {
    styleListRef.current
      ?.querySelector('.tableSelectedRow')
      ?.scrollIntoView({ block: 'nearest' });
  }, [selectedStyle?.id]);
  const [isEditingStyleName, setIsEditingStyleName] = useState(false);
  let styleList = [...tablesStyles];
  const { visibleStyles, filter: sectionStylesFilter } =
    useSectionStylesFilter(styleList);

  const styleData = [
    {
      title: 'Actions',
      dataIndex: 'actions',
      key: 'actions',
      width: (18 + 16) * 3 + 16,
      onHeaderCell: styleListHeaderCell,
    },
    {
      title: 'Style',
      dataIndex: 'style',
      key: 'style',
      onHeaderCell: styleListHeaderCell,
      onCell: (element, rowIndex) => {
        return {
          key: `style-config-${rowIndex}`,
          onClick: (event) => {
            handleSelectedStyle(element);
          },
        };
      },
    }
  ];

  const handleRemoveStyle = (idToRemove) => {
    setConfiguration(removeStyle(configuration, 'tableStyles', idToRemove));
  };

  const handleRenameStyle = (ID, event) => {
    const patch = renameStyle(configuration, 'tableStyles', ID, event.target.value);
    setConfiguration(patch);
    setIsEditingStyleName(null);
    setEditedName('');
    setSelectedStyle(patch.tableStyles.find((element) => element.id === ID));
  };

  const handleCopyStyle = (copiedId) => {
    setConfiguration(copyStyle(configuration, 'tableStyles', copiedId).patch);
  };

  const [editedName, setEditedName] = useState('');
  const dataSourceMapped = [];

  const iconStyle = {
    margin: '.5rem',
    fontSize: '1.5em',
  }
  visibleStyles.forEach((element) => {
    const locks = styleLocks(element);
    const cantEdit = locks.remove;
    const tableStyleLocked = locks.copy;
    dataSourceMapped.push({
      key: `tableStyle-${element.id}`,
      id: element.id,
      style:
        isEditingStyleName === element.id ? (
          <Input
            value={editedName}
            id='styleNameInput2'
            style={{ width: '60%' }}
            onChange={(e) => setEditedName(e.target.value)}
            onPressEnter={(e) => {
              if (!cantEdit && e.target.value.length > 0) {
                handleRenameStyle(element.id, e);
              }
            }}
            onBlur={(e) => {
              if (!cantEdit && e.target.value.length > 0) {
                handleRenameStyle(element.id, e);
              }
            }}
          />
        ) : (
          element.name
        ),
      actions: (
        <div>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Renommer'}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'edit', style: 'light' })}
              style={{
                ...iconStyle,
                color: cantEdit ? 'lightgray' : 'black',
              }}
              onClick={cantEdit ? null : (e) => {
                setIsEditingStyleName(element.id);
                setEditedName(element.name);
              }}
            />
          </Tooltip>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Dupliquer'}
          >
            <FontAwesomeIcon
              icon={icon({ name: 'copy', style: 'light' })}
              style={{
                ...iconStyle,
                color: tableStyleLocked ? 'lightgray' : 'black',
              }}
              onClick={tableStyleLocked ? null : (e) => handleCopyStyle(element.id)}
            />
          </Tooltip>
          <Tooltip
            placement='bottom'
            mouseLeaveDelay='0'
            title={'Supprimer'}
          >
            {cantEdit ? (
              <FontAwesomeIcon
                icon={icon({ name: 'trash', style: 'light' })}
                style={{
                  ...iconStyle,
                  color: 'lightgray'
                }}
              />
            ) : (
              <Popconfirm
                title='Voulez vous vraiment supprimer ce style ?'
                onConfirm={() => { handleRemoveStyle(element.id); }}
                okText='Oui'
                cancelText='Non'
              >
                <FontAwesomeIcon
                  icon={icon({ name: 'trash', style: 'light' })}
                  style={{
                    ...iconStyle,
                    color: 'red'
                  }}
                />
              </Popconfirm>
            )}
          </Tooltip>
        </div>
      ),
    });
  });

  const [isAddTableStyleLoading, setIsAddTableStyleLoading] = useState(false);

  const handleSelectedStyle = (e) => {
    tablesStyles.forEach((element) => {
      if (element.id === e.id) {
        setSelectedStyle(element);
      }
    });
    return selectedStyle;
  };

  const loadingAnimation = () => {
    setIsAddTableStyleLoading(true);
    setTimeout(() => {
      setIsAddTableStyleLoading(false);
    }, 1000);
  };

  const handleSaveChanges = (updatedTableStyle) => {
    setSelectedStyle(updatedTableStyle);
    setConfiguration(updateStyle(configuration, 'tableStyles', updatedTableStyle));
  };

  const handleAddTableStyle = (newStyleName) => {
    loadingAnimation();
    const { patch, style } = addStyle(configuration, 'tableStyles', {
      ...defaultTableConfiguration,
      name: newStyleName,
    });
    setConfiguration(patch);
    setSelectedStyle(style);
  };

  const handleCustomTextColor = (newColor, strippedNumber) => {
    const color = configuration.colors.find(
      (color) => color.label === newColor
    );
    if (strippedNumber === 1) {
      handleSaveChanges({
        ...selectedStyle,
        customTextColor1: color,
      });
    } else if (strippedNumber === 2) {
      handleSaveChanges({
        ...selectedStyle,
        customTextColor2: color,
        alternateTextColors: true,
      });
    }
  };

  // Le site web ne lit l'épaisseur et le type de trait que sous ce drapeau
  // (cf. proposalToWebData) : il suit donc tout réglage de la bordure.
  const handleSaveBorder = (patch) => {
    handleSaveChanges({ ...selectedStyle, ...patch, customBorderBool: true });
  };

  const handleCustomBorderColor = (newColor) => {
    const color = configuration.colors.find(
      (color) => color.label === newColor
    );
    handleSaveChanges({ ...selectedStyle, customBorderColor: color });
  };
  const paddingUnit = resolveCustomPadding(selectedStyle).unit;
  // Change l'unité sans changer l'espacement à l'écran.
  const handlePaddingUnit = (unit) => {
    const { value } = resolveCustomPadding(selectedStyle);
    const cellFontSize = selectedStyle?.fontSizeCells || configuration.fontSize;
    handleSaveChanges({
      ...selectedStyle,
      customCellPaddingUnit: unit,
      customCellPadding: convertPaddingUnit(value, paddingUnit, unit, cellFontSize),
    });
  };
  const handleFontSizeCells = (newFontSize) => {
    handleSaveChanges({ ...selectedStyle, fontSizeCells: newFontSize });
  }
  const handleFontSizeHeader = (newFontSize) => {
    handleSaveChanges({ ...selectedStyle, fontSizeHeader: newFontSize });
  }
  const handleFontFamilyCells = (newFontFamily) => {
    handleSaveChanges({ ...selectedStyle, fontFamilyCells: newFontFamily });
  }
  const handleFontFamilyHeader = (newFontFamily) => {
    handleSaveChanges({ ...selectedStyle, fontFamilyHeader: newFontFamily });
  }
  const handleCustomHeaderColor = (newColor) => {
    const color = configuration.colors.find(
      (color) => color.label === newColor
    );
    handleSaveChanges({ ...selectedStyle, customHeaderColor: color });
  };

  const handleCustomStrippedClr = (newColor, strippedNumber) => {
    const color = configuration.colors.find(
      (color) => color.label === newColor
    );
    if (selectedStyle.stripped1 === false) {
      handleSaveChanges({ ...selectedStyle, customCellColor: color });
    } else if (strippedNumber === 1 && selectedStyle.stripped1) {
      handleSaveChanges({ ...selectedStyle, strippedCustomClr1: color });
    } else if (strippedNumber === 2 && selectedStyle.stripped1) {
      handleSaveChanges({ ...selectedStyle, strippedCustomClr2: color });
    }
  };

  const handleCustomHeaderTextColor = (newColor) => {
    const color = configuration.colors.find(
      (color) => color.label === newColor
    );
    handleSaveChanges({ ...selectedStyle, customHeaderTextColor: color });
  };

  return (
    <div>
      <div style={{ textAlign: 'center' }}>
        <h5>Liste des styles de tableaux</h5>
        <NewStyleInput onAdd={handleAddTableStyle} loading={isAddTableStyleLoading} style={{ margin: '1rem 0' }} />
        {sectionStylesFilter}
        {/* Hauteur bornée : la liste s'allonge avec les styles et poussait
            leurs réglages hors de vue. Elle défile désormais seule. */}
        <div ref={styleListRef}>
          <Table
            columns={styleData}
            dataSource={dataSourceMapped}
            rowClassName={(record, _rowIndex) => { return record.style === selectedStyle.name ? 'tableSelectedRow' : '' }}
            pagination={false}
            bordered
            scroll={{ y: STYLE_LIST_MAX_HEIGHT }}
          />
        </div>
      </div>
      <div style={{ margin: '1.5rem 0' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <SettingsCard
            icon='text'
            title='En-tête'
            badges={<ContrastBadge ratio={headerContrast(selectedStyle)} />}
          >
            <Form.Item {...cardFieldLayout} label={t('font-size')}>
              <InputNumber
                disabled={selectedStyle?.fromTmp}
                min={minFontSize}
                max={maxFontSize}
                value={selectedStyle?.fontSizeHeader || configuration.fontSize}
                onChange={handleFontSizeHeader}
                style={{ width: CARD_NUMBER_WIDTH }}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label={t('font-family')}>
              <FontPicker
                disabled={selectedStyle?.fromTmp}
                width={CARD_SELECT_WIDTH}
                onChange={handleFontFamilyHeader}
                forcedValue={selectedStyle?.fontFamilyHeader || configuration.fontFamily}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Couleur de fond'>
              <ColorPaletteButton
                title="Couleur de fond de l'en-tête"
                icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                disabled={selectedStyle?.fromTmp}
                value={selectedStyle.customHeaderColor}
                colors={configuration.colors}
                onChange={handleCustomHeaderColor}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Couleur du texte'>
              <ColorPaletteButton
                title="Couleur du texte de l'en-tête"
                disabled={selectedStyle?.fromTmp}
                value={selectedStyle.customHeaderTextColor}
                colors={configuration.colors}
                onChange={handleCustomHeaderTextColor}
              />
            </Form.Item>
          </SettingsCard>
          <SettingsCard
            icon='table-rows'
            title='Corps du tableau'
            badges={<>
              {selectedStyle.stripped1 && <Tag>Zébré</Tag>}
              <ContrastBadge ratio={cellsContrast(selectedStyle)} />
            </>}
          >
            <Form.Item {...cardFieldLayout} label={t('font-size')}>
              <InputNumber
                disabled={selectedStyle?.fromTmp}
                min={minFontSize}
                max={maxFontSize}
                value={selectedStyle?.fontSizeCells || configuration.fontSize}
                onChange={handleFontSizeCells}
                style={{ width: CARD_NUMBER_WIDTH }}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label={t('font-family')}>
              <FontPicker
                disabled={selectedStyle?.fromTmp}
                width={CARD_SELECT_WIDTH}
                onChange={handleFontFamilyCells}
                forcedValue={selectedStyle?.fontFamilyCells || configuration.fontFamily}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Lignes alternées'>
              <Switch
                disabled={selectedStyle?.fromTmp}
                checked={selectedStyle.stripped1}
                onChange={() => {
                  let newStyle = { ...selectedStyle, stripped1: !selectedStyle.stripped1 };
                  if (!selectedStyle.stripped1) {
                    const color = configuration.colors.find(
                      (color) => color.label === 'white'
                    );
                    newStyle.strippedCustomClr1 = color;
                    newStyle.strippedCustomClr2 = color;
                  }
                  handleSaveChanges(newStyle)
                }}
              />
            </Form.Item>
            <Form.Item
              {...cardFieldLayout}
              label={selectedStyle.stripped1 ? 'Couleurs des lignes' : 'Couleur des lignes'}
            >
              {/* Deux boutons de même icône quand l'alternance est active :
                  seule l'infobulle dit lequel colore quelle ligne. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <ColorPaletteButton
                  title={selectedStyle.stripped1 ? 'Couleur des lignes impaires' : 'Couleur des lignes'}
                  icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                  disabled={selectedStyle?.fromTmp}
                  value={
                    selectedStyle.stripped1
                      ? selectedStyle.strippedCustomClr1
                      : selectedStyle.customCellColor
                  }
                  colors={configuration.colors}
                  onChange={(e) => handleCustomStrippedClr(e, 1)}
                />
                {selectedStyle.stripped1 ? (
                  <ColorPaletteButton
                    title='Couleur des lignes paires'
                    icon={<DuotoneLightIcon name='fill-drip' style={iconButtonStyle()} />}
                    disabled={selectedStyle?.fromTmp}
                    value={selectedStyle.strippedCustomClr2}
                    colors={configuration.colors}
                    onChange={(e) => handleCustomStrippedClr(e, 2)}
                  />
                ) : null}
              </div>
            </Form.Item>
            <Form.Item
              {...cardFieldLayout}
              label={selectedStyle.stripped1 ? 'Couleurs du texte' : 'Couleur du texte'}
            >
              {/* Zébré, un texte par couleur de ligne. Tant que le second n'a
                  pas été choisi, les lignes paires gardent le premier. */}
              <div style={{ display: 'flex', gap: 8 }}>
                <ColorPaletteButton
                  title={selectedStyle.stripped1 ? 'Couleur du texte des lignes impaires' : 'Couleur du texte de cellules'}
                  disabled={selectedStyle?.fromTmp}
                  value={selectedStyle.customTextColor1}
                  colors={configuration.colors}
                  onChange={(e) => handleCustomTextColor(e, 1)}
                />
                {selectedStyle.stripped1 ? (
                  <ColorPaletteButton
                    title='Couleur du texte des lignes paires'
                    disabled={selectedStyle?.fromTmp}
                    value={
                      selectedStyle.alternateTextColors
                        ? selectedStyle.customTextColor2
                        : selectedStyle.customTextColor1
                    }
                    colors={configuration.colors}
                    onChange={(e) => handleCustomTextColor(e, 2)}
                  />
                ) : null}
              </div>
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Taille du tableau'>
              {/* Personnalisée : l'espacement se saisit à droite du sélecteur,
                  dans l'unité de son choix. */}
              <Space.Compact>
                <Select
                  disabled={selectedStyle?.fromTmp}
                  value={selectedStyle?.size}
                  onChange={(size) => {
                    handleSaveChanges({
                      ...selectedStyle,
                      size,
                      // Passage en personnalisé : on part de l'espacement de la
                      // taille quittée, le tableau ne saute pas.
                      ...(size === 'custom' && selectedStyle.size !== 'custom' && {
                        customCellPadding: convertPadding(selectedStyle.size).vertical,
                        customCellPaddingUnit: 'px',
                      }),
                    });
                  }}
                  style={{ width: selectedStyle?.size === 'custom' ? CARD_CUSTOM_SELECT_WIDTH : CARD_SELECT_WIDTH }}
                  options={[
                    { label: 'Petit', value: 'small' },
                    { label: 'Moyen', value: 'middle' },
                    { label: 'Grand', value: 'default' },
                    { label: 'Personnalisé', value: 'custom' },
                  ]}
                />
                {selectedStyle?.size === 'custom' && (
                  <>
                    <InputNumber
                      disabled={selectedStyle?.fromTmp}
                      min={0}
                      max={paddingUnit === 'px' ? 40 : 4}
                      step={paddingUnit === 'px' ? 1 : 0.1}
                      placeholder='Valeur'
                      title='Espacement dans les cellules'
                      value={selectedStyle.customCellPadding ?? null}
                      onChange={(customCellPadding) => handleSaveChanges({
                        ...selectedStyle,
                        customCellPadding: customCellPadding ?? null,
                      })}
                      style={{ width: CARD_CUSTOM_VALUE_WIDTH }}
                    />
                    <Select
                      disabled={selectedStyle?.fromTmp}
                      value={paddingUnit}
                      onChange={handlePaddingUnit}
                      style={{ width: CARD_UNIT_SELECT_WIDTH }}
                      options={TABLE_PADDING_UNITS.map((unit) => ({ label: unit, value: unit }))}
                    />
                  </>
                )}
              </Space.Compact>
            </Form.Item>
          </SettingsCard>
          <SettingsCard
            icon='border-outer'
            title='Bordure'
            toggle={{
              checked: !!selectedStyle.bordered,
              disabled: selectedStyle?.fromTmp,
              onChange: () => {
                let newStyle = {
                  ...selectedStyle,
                  bordered: !selectedStyle.bordered
                };
                if (!selectedStyle.bordered) {
                  const color = configuration.colors.find(
                    (color) => color.label === 'white'
                  );
                  newStyle.customBorderColor = color;
                }
                handleSaveChanges(newStyle);
              },
            }}
          >
            <Form.Item {...cardFieldLayout} label='Couleur de la bordure'>
              <ColorPaletteButton
                title="Couleur de la bordure"
                icon={<DuotoneLightIcon name='border-outer' style={iconButtonStyle()} />}
                colorInIcon='primary'
                value={selectedStyle.customBorderColor}
                colors={configuration.colors}
                onChange={handleCustomBorderColor}
                disabled={selectedStyle?.fromTmp}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Épaisseur'>
              <InputNumber
                disabled={selectedStyle?.fromTmp}
                min={1}
                max={6}
                value={Number(selectedStyle.customBorderWidth) || DEFAULT_TABLE_BORDER_WIDTH}
                onChange={(width) => handleSaveBorder({ customBorderWidth: width ?? DEFAULT_TABLE_BORDER_WIDTH })}
                addonAfter='px'
                style={{ width: CARD_UNIT_WIDTH }}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Type de trait'>
              <Select
                disabled={selectedStyle?.fromTmp}
                value={selectedStyle.customBorderType || 'solid'}
                onChange={(type) => handleSaveBorder({ customBorderType: type })}
                style={{ width: CARD_SELECT_WIDTH }}
                options={[
                  { label: 'Plein', value: 'solid' },
                  { label: 'Pointillés', value: 'dotted' },
                  { label: 'Tirets', value: 'dashed' },
                ]}
              />
            </Form.Item>
            <Form.Item {...cardFieldLayout} label='Traits horizontaux'>
              <Switch
                disabled={selectedStyle?.fromTmp}
                checked={!!selectedStyle.borderHorizontalOnly}
                onChange={(borderHorizontalOnly) => handleSaveChanges({ ...selectedStyle, borderHorizontalOnly })}
              />
            </Form.Item>
          </SettingsCard>
          <SettingsCard
            icon='input-text'
            title='Pied de tableau'
            toggle={{
              checked: !!selectedStyle.footer,
              disabled: selectedStyle?.fromTmp,
              onChange: () =>
                handleSaveChanges({
                  ...selectedStyle,
                  footer: !selectedStyle.footer,
                }),
            }}
          >
            <Form.Item
              {...cardFieldLayout}
              wrapperCol={{ flex: 1 }}
              label='Texte du pied'
              style={{ flex: '1 1 320px' }}
            >
              <Input
                placeholder='Texte'
                value={selectedStyle.footerData}
                disabled={selectedStyle?.fromTmp}
                onPressEnter={(e) => {
                  e.preventDefault();
                  handleSaveChanges({
                    ...selectedStyle,
                    footerData: e.target.value,
                  });
                }}
                onChange={(e) => {
                  e.preventDefault();
                  handleSaveChanges({
                    ...selectedStyle,
                    footerData: e.target.value,
                  });
                }}
              />
            </Form.Item>
          </SettingsCard>
        </div>
      </div>
    </div>
  );
};
